import {
  LoaderCircle,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
  Tags,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import CategoriaProductoModal from '../components/bodega/CategoriaProductoModal'
import ModalMensaje from '../components/ui/ModalMensaje'
import { useEmpresa } from '../context/EmpresaContext'
import { useModalMensaje } from '../hooks/useModalMensaje'
import { supabase } from '../lib/supabase'

function formatearFecha(valor) {
  if (!valor) return '—'

  const fecha = new Date(valor)

  if (Number.isNaN(fecha.getTime())) return '—'

  return fecha.toLocaleDateString('es-CL')
}

function CategoriasProducto() {
  const { empresaActual } = useEmpresa()
  const { modal, abrirModal, actualizarModal } = useModalMensaje()

  const esAdministrador = empresaActual?.rol === 'administrador'

  const [categorias, setCategorias] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [modalAbierto, setModalAbierto] = useState(false)
  const [categoriaEditar, setCategoriaEditar] = useState(null)
  const [actualizandoId, setActualizandoId] = useState(null)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!empresaActual?.id) {
      setCategorias([])
      setCargando(false)
      return
    }

    cargarCategorias()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  const cargarCategorias = async () => {
    try {
      setCargando(true)
      setError('')

      const { data, error: errorConsulta } = await supabase
        .from('categorias_producto')
        .select('*')
        .eq('empresa_id', empresaActual.id)
        .order('nombre')

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      setCategorias(data || [])
    } catch (errorCarga) {
      console.error(
        'Error al cargar categorías de producto:',
        errorCarga
      )

      if (!montadoRef.current) return

      setError(
        errorCarga.message ||
          'No fue posible cargar las categorías.'
      )
      setCategorias([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const categoriasFiltradas = categorias.filter((categoria) => {
    const termino = busqueda.trim().toLowerCase()

    if (!termino) return true

    const nombreCoincide = (categoria.nombre || '')
      .toLowerCase()
      .includes(termino)

    const descripcionCoincide = (categoria.descripcion || '')
      .toLowerCase()
      .includes(termino)

    return nombreCoincide || descripcionCoincide
  })

  const abrirCreacion = () => {
    setCategoriaEditar(null)
    setModalAbierto(true)
  }

  const abrirEdicion = (categoria) => {
    setCategoriaEditar(categoria)
    setModalAbierto(true)
  }

  const cerrarModalCategoria = () => {
    setModalAbierto(false)
    setCategoriaEditar(null)
  }

  const alGuardar = (categoriaGuardada) => {
    const fueEdicion = Boolean(categoriaEditar)

    cerrarModalCategoria()
    cargarCategorias()

    abrirModal({
      tipo: 'success',
      titulo: fueEdicion
        ? 'Categoría actualizada'
        : 'Categoría creada',
      mensaje: `La categoría "${categoriaGuardada?.nombre}" fue ${
        fueEdicion ? 'actualizada' : 'creada'
      } correctamente.`,
    })
  }

  const aplicarCambioEstado = async (
    categoria,
    activar,
    esDesactivacion
  ) => {
    if (esDesactivacion) {
      actualizarModal({
        procesando: true,
        mostrarCancelar: false,
        textoAceptar: 'Desactivando...',
      })
    }

    try {
      setActualizandoId(categoria.id)

      const { error: errorActualizar } = await supabase
        .from('categorias_producto')
        .update({ activo: activar })
        .eq('id', categoria.id)
        .eq('empresa_id', empresaActual.id)

      if (errorActualizar) {
        throw errorActualizar
      }

      if (!montadoRef.current) return

      await cargarCategorias()

      if (esDesactivacion) {
        abrirModal({
          tipo: 'success',
          titulo: 'Categoría desactivada',
          mensaje: 'La categoría fue desactivada correctamente.',
        })
      }
    } catch (errorEstado) {
      console.error(
        'Error al actualizar estado de la categoría:',
        errorEstado
      )

      if (!montadoRef.current) return

      abrirModal({
        tipo: 'error',
        titulo: 'No se pudo actualizar',
        mensaje:
          errorEstado.message ||
          'No fue posible actualizar el estado de la categoría.',
      })
    } finally {
      if (montadoRef.current) {
        setActualizandoId(null)
      }
    }
  }

  const cambiarEstado = (categoria) => {
    const activar = !categoria.activo

    if (!activar) {
      abrirModal({
        tipo: 'confirm',
        titulo: 'Desactivar categoría',
        mensaje: `¿Deseas desactivar la categoría "${categoria.nombre}"?`,
        textoAceptar: 'Desactivar',
        textoCancelar: 'Cancelar',
        mostrarCancelar: true,
        onAceptar: () =>
          aplicarCambioEstado(categoria, false, true),
      })
      return
    }

    aplicarCambioEstado(categoria, true, false)
  }

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <LoaderCircle
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando categorías...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>

          <button
            type="button"
            onClick={cargarCategorias}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (categorias.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <Tags
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            No hay categorías registradas
          </h3>

          <p className="mt-2 text-slate-500">
            {esAdministrador
              ? 'Presiona “Nueva categoría” para ingresar el primer registro.'
              : 'Aún no se han registrado categorías de producto.'}
          </p>

          {esAdministrador && (
            <button
              type="button"
              onClick={abrirCreacion}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-600 px-5 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50"
            >
              <Plus size={19} />
              Ingresar primera categoría
            </button>
          )}
        </div>
      )
    }

    return (
      <>
        <div className="relative mb-4 max-w-sm">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />

          <input
            type="text"
            value={busqueda}
            onChange={(event) => setBusqueda(event.target.value)}
            placeholder="Buscar por nombre o descripción"
            className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          />
        </div>

        {categoriasFiltradas.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <p className="text-slate-500">
              No se encontraron categorías para “{busqueda}”.
            </p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm md:min-w-[720px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Nombre</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Descripción
                  </th>
                  <th className="px-6 py-3">Estado</th>
                  <th className="hidden px-6 py-3 lg:table-cell">
                    Fecha de creación
                  </th>
                  {esAdministrador && (
                    <th className="px-6 py-3 text-right">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {categoriasFiltradas.map((categoria) => (
                  <tr key={categoria.id}>
                    <td className="px-6 py-4 font-semibold text-slate-900">
                      {categoria.nombre}
                    </td>

                    <td
                      className="hidden max-w-xs truncate px-6 py-4 text-slate-600 md:table-cell"
                      title={categoria.descripcion || ''}
                    >
                      {categoria.descripcion || '—'}
                    </td>

                    <td className="px-6 py-4">
                      {categoria.activo ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                          Inactivo
                        </span>
                      )}
                    </td>

                    <td className="hidden px-6 py-4 text-slate-600 lg:table-cell">
                      {formatearFecha(categoria.creado_en)}
                    </td>

                    {esAdministrador && (
                      <td className="px-6 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              abrirEdicion(categoria)
                            }
                            title="Editar"
                            aria-label="Editar categoría"
                            className="rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:bg-slate-100"
                          >
                            <Pencil size={16} />
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              cambiarEstado(categoria)
                            }
                            disabled={
                              actualizandoId === categoria.id
                            }
                            title={
                              categoria.activo
                                ? 'Desactivar'
                                : 'Reactivar'
                            }
                            aria-label={
                              categoria.activo
                                ? 'Desactivar categoría'
                                : 'Reactivar categoría'
                            }
                            className={
                              categoria.activo
                                ? 'rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60'
                                : 'rounded-lg border border-emerald-200 p-2 text-emerald-600 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60'
                            }
                          >
                            {actualizandoId === categoria.id ? (
                              <LoaderCircle
                                size={16}
                                className="animate-spin"
                              />
                            ) : categoria.activo ? (
                              <PowerOff size={16} />
                            ) : (
                              <Power size={16} />
                            )}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Mantenedores
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Categorías de productos
          </h2>

          <p className="mt-2 text-slate-500">
            Administra las categorías de producto de la empresa
            seleccionada.
          </p>
        </div>

        {esAdministrador && (
          <button
            type="button"
            onClick={abrirCreacion}
            className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700"
          >
            <Plus size={20} />
            Nueva categoría
          </button>
        )}
      </div>

      {renderContenido()}

      {modalAbierto && (
        <CategoriaProductoModal
          categoria={categoriaEditar}
          onClose={cerrarModalCategoria}
          onGuardado={alGuardar}
        />
      )}

      <ModalMensaje {...modal} />
    </div>
  )
}

export default CategoriasProducto
